
import { useCallback, useEffect, useRef } from 'react';
import { useEntityRealtime, EntityRealtimeOptions } from '../core/useEntityRealtime';
import {
  SocketEvent,
  ThreadReactionAddedPayload,
  ThreadReactionRemovedPayload,
  CommentReactionAddedPayload,
  CommentReactionRemovedPayload,
} from '@scrambl/shared';
import { useThreadStore } from '@/store/thread.store';
// Debug mode flag
const isBrowser = typeof window !== 'undefined' && typeof localStorage !== 'undefined';
const DEBUG_MODE = import.meta.env.DEV &&
  isBrowser &&
  (localStorage.getItem('REALTIME_DEBUG') === 'true' || localStorage.getItem('DEBUG_ALL') === 'true');

// Global reaction event tracker
const processedReactionEvents = new Map<string, number>();
const REACTION_EVENT_TTL = 10000; // 10 seconds

// Clean up old events periodically
if (typeof window !== 'undefined') {
  setInterval(() => {
    const now = Date.now();
    processedReactionEvents.forEach((timestamp, eventId) => {
      if (now - timestamp > REACTION_EVENT_TTL) {
        processedReactionEvents.delete(eventId);
      }
    });
  }, 60000); // Check every minute
}

// Helper functions for conditional logging
function debugLog(..._args: unknown[]) {
  // Debug logging disabled - enable REALTIME_DEBUG in localStorage
}

export interface UseReactionsRealtimeOptions extends EntityRealtimeOptions {
  onThreadReactionAdded?: (reaction: ThreadReactionAddedPayload) => void;
  onThreadReactionRemoved?: (data: ThreadReactionRemovedPayload) => void;
  onCommentReactionAdded?: (reaction: CommentReactionAddedPayload) => void;
  onCommentReactionRemoved?: (data: CommentReactionRemovedPayload) => void;
}

/**
 * Hook for real-time reactions that builds on the entity realtime hook
 * Uses standardized client-generated entity tracking and centralized debugging
 */
export function useReactionsRealtime(threadId: string, options: UseReactionsRealtimeOptions = {}) {
  const { 
    handleThreadReactionAdded, 
    handleThreadReactionRemoved,
    handleCommentReactionAdded,
    handleCommentReactionRemoved
  } = useThreadStore();
  
  // Track options for closures
  const optionsRef = useRef(options);
  
  // For debugging - track active instances
  const instanceId = useRef(`reactions-${Math.random().toString(36).substring(2, 8)}`);
  
  // Batch updates for improved performance
  const pendingUpdatesRef = useRef<{
    threadReactionAdded: ThreadReactionAddedPayload[],
    threadReactionRemoved: ThreadReactionRemovedPayload[],
    commentReactionAdded: CommentReactionAddedPayload[],
    commentReactionRemoved: CommentReactionRemovedPayload[]
  }>({
    threadReactionAdded: [],
    threadReactionRemoved: [],
    commentReactionAdded: [],
    commentReactionRemoved: []
  });
  
  // Update options ref when options change
  useEffect(() => {
    optionsRef.current = options;
  }, [options]);
  
  // Configure entity options
  const entityOptions: EntityRealtimeOptions = {
    ...options
  };
  
  // Use entity hook for common functionality
  const {
    isConnected,
    trackClientEntity,
    isClientGenerated,
    addEventListener,
    socket,
    markEventProcessed: markEntityEventProcessed,
    hasProcessedEvent: hasProcessedEntityEvent
  } = useEntityRealtime('thread', threadId, entityOptions);
  
  // Global event processed check that works across hook instances
  const hasProcessedEvent = useCallback((eventId: string): boolean => {
    return processedReactionEvents.has(eventId) || hasProcessedEntityEvent(eventId);
  }, [hasProcessedEntityEvent]);
  
  // Global event processing tracking
  const markEventProcessed = useCallback((eventId: string): void => {
    processedReactionEvents.set(eventId, Date.now());
    markEntityEventProcessed(eventId);
  }, [markEntityEventProcessed]);
  
  // Process batched updates at a regular interval
  useEffect(() => {
    // Skip if no thread ID
    if (!threadId) return;
    
    const interval = setInterval(() => {
      const updates = pendingUpdatesRef.current;
      
      if (updates.threadReactionAdded.length === 0 && 
          updates.threadReactionRemoved.length === 0 &&
          updates.commentReactionAdded.length === 0 &&
          updates.commentReactionRemoved.length === 0) {
        return;
      }
      
      debugLog(`[useReactionsRealtime:${instanceId.current}] Processing batched updates`, {
        threadReactions: updates.threadReactionAdded.length,
        removedThreadReactions: updates.threadReactionRemoved.length,
        commentReactions: updates.commentReactionAdded.length,
        removedCommentReactions: updates.commentReactionRemoved.length
      });
      
      // Process thread reactions
      updates.threadReactionAdded.forEach(reaction => {
        // Process in store
        handleThreadReactionAdded(reaction);
        
        // Call callback if provided
        if (optionsRef.current.onThreadReactionAdded) {
          optionsRef.current.onThreadReactionAdded(reaction);
        }
      });
      
      // Process removed thread reactions
      updates.threadReactionRemoved.forEach(data => {
        // Process in store
        handleThreadReactionRemoved(data);
        
        // Call callback if provided
        if (optionsRef.current.onThreadReactionRemoved) {
          optionsRef.current.onThreadReactionRemoved(data);
        }
      });
      
      // Process comment reactions
      updates.commentReactionAdded.forEach(reaction => {
        // Process in store
        handleCommentReactionAdded(reaction);
        
        // Call callback if provided
        if (optionsRef.current.onCommentReactionAdded) {
          optionsRef.current.onCommentReactionAdded(reaction);
        }
      });
      
      // Process removed comment reactions
      updates.commentReactionRemoved.forEach(data => {
        // Process in store
        handleCommentReactionRemoved(data);
        
        // Call callback if provided
        if (optionsRef.current.onCommentReactionRemoved) {
          optionsRef.current.onCommentReactionRemoved(data);
        }
      });
      
      // Clear pending updates
      pendingUpdatesRef.current = {
        threadReactionAdded: [],
        threadReactionRemoved: [],
        commentReactionAdded: [],
        commentReactionRemoved: []
      };
    }, 300); // Process batches every 300ms
    
    return () => clearInterval(interval);
  }, [
    threadId,
    handleThreadReactionAdded,
    handleThreadReactionRemoved,
    handleCommentReactionAdded,
    handleCommentReactionRemoved
  ]);
  
  // Set up reaction-specific event handlers
  const setupReactionEvents = useCallback(() => {
    if (!socket || !threadId) return () => {};
    
    if (DEBUG_MODE && !optionsRef.current.suppressLogging) {
      debugLog(`[Reactions:${threadId}] Setting up handlers`);
    }
    
    // Thread reaction handlers
    const handleThreadReactionAddedEvent = (reaction: ThreadReactionAddedPayload) => {
      // Skip if not for this thread
      if (reaction.threadId !== threadId) {
        return;
      }
      
      // Create a unique event ID
      const eventId = `thread-reaction-added-${reaction.id}`;
      
      // Skip if already processed or is client-generated
      if (hasProcessedEvent(eventId)) {
        debugLog(`[useReactionsRealtime:${instanceId.current}] Skipping already processed reaction: ${reaction.id}`);
        return;
      }
      
      if (isClientGenerated(reaction.id)) {
        debugLog(`[useReactionsRealtime:${instanceId.current}] Skipping client-generated reaction: ${reaction.id}`);
        return;
      }
      
      // Mark as processed
      markEventProcessed(eventId);
      
      debugLog(`[useReactionsRealtime:${instanceId.current}] Queueing thread reaction for batch processing: ${reaction.id}`);
      
      // Add to batch instead of processing immediately
      pendingUpdatesRef.current.threadReactionAdded.push(reaction);
    };
    
    const handleThreadReactionRemovedEvent = (data: ThreadReactionRemovedPayload) => {
      // Skip if not for this thread
      if (data.threadId !== threadId) {
        return;
      }
      
      // Create a unique event ID
      const eventId = `thread-reaction-removed-${data.id}`;
      
      // Skip if already processed
      if (hasProcessedEvent(eventId)) {
        debugLog(`[useReactionsRealtime:${instanceId.current}] Skipping already processed reaction removal: ${data.id}`);
        return;
      }
      
      // Mark as processed
      markEventProcessed(eventId);
      
      debugLog(`[useReactionsRealtime:${instanceId.current}] Queueing thread reaction removal for batch processing: ${data.id}`);
      
      // Add to batch
      pendingUpdatesRef.current.threadReactionRemoved.push(data);
    };
    
    // Comment reaction handlers
    const handleCommentReactionAddedEvent = (reaction: CommentReactionAddedPayload & { _source?: string }) => {
      // Skip if not for this thread
      if (reaction.threadId !== threadId) {
        return;
      }
      
      // Skip reactions coming from client toggles as they're already processed
      if (reaction._source === 'client-toggle') {
        debugLog(`[Reactions:${threadId}] Skipping client-toggle reaction: ${reaction.id}`);
        return;
      }
      
      // Create a unique event ID
      const eventId = `comment-reaction-added-${reaction.id}`;
      
      // Skip if already processed or is client-generated
      if (hasProcessedEvent(eventId)) {
        debugLog(`[useReactionsRealtime:${instanceId.current}] Skipping already processed comment reaction: ${reaction.id}`);
        return;
      }
      
      if (isClientGenerated(reaction.id)) {
        debugLog(`[useReactionsRealtime:${instanceId.current}] Skipping client-generated comment reaction: ${reaction.id}`);
        return;
      }
      
      // Mark as processed
      markEventProcessed(eventId);
      
      debugLog(`[useReactionsRealtime:${instanceId.current}] Queueing comment reaction for batch processing: ${reaction.id}`);
      
      // Add to batch
      pendingUpdatesRef.current.commentReactionAdded.push(reaction);
    };
    
    const handleCommentReactionRemovedEvent = (data: CommentReactionRemovedPayload & { _source?: string }) => {
      // Skip if not for this thread
      if (data.threadId !== threadId) {
        return;
      }
      
      // Skip reactions coming from client toggles as they're already processed
      if (data._source === 'client-toggle') {
        debugLog(`[Reactions:${threadId}] Skipping client-toggle reaction removal: ${data.id}`);
        return;
      }
      
      // Create a unique event ID
      const eventId = `comment-reaction-removed-${data.id}`;
      
      // Skip if already processed
      if (hasProcessedEvent(eventId)) {
        debugLog(`[useReactionsRealtime:${instanceId.current}] Skipping already processed comment reaction removal: ${data.id}`);
        return;
      }
      
      // Mark as processed
      markEventProcessed(eventId);
      
      debugLog(`[useReactionsRealtime:${instanceId.current}] Queueing comment reaction removal for batch processing: ${data.id}`);
      
      // Add to batch
      pendingUpdatesRef.current.commentReactionRemoved.push(data);
    };
    
    // Set up event listeners using addEventListener from useEntityRealtime
    // This will handle broadcasting to other tabs automatically
    const cleanupReactionAdd = addEventListener(SocketEvent.THREAD_REACTION_ADDED, handleThreadReactionAddedEvent);
    const cleanupReactionRemove = addEventListener(SocketEvent.THREAD_REACTION_REMOVED, handleThreadReactionRemovedEvent);
    const cleanupCommentReactionAdd = addEventListener(SocketEvent.COMMENT_REACTION_ADDED, handleCommentReactionAddedEvent);
    const cleanupCommentReactionRemove = addEventListener(SocketEvent.COMMENT_REACTION_REMOVED, handleCommentReactionRemovedEvent);
    
    // Return cleanup function
    return () => {
      debugLog(`[useReactionsRealtime:${instanceId.current}] Cleaning up reaction handlers`);
      cleanupReactionAdd();
      cleanupReactionRemove();
      cleanupCommentReactionAdd();
      cleanupCommentReactionRemove();
    };
  }, [
    socket,
    threadId,
    hasProcessedEvent,
    markEventProcessed,
    isClientGenerated,
    addEventListener
  ]);
  
  // Set up thread-specific event handlers
  useEffect(() => {
    if (!socket || !threadId) return;
    
    const cleanup = setupReactionEvents();
    return () => cleanup();
  }, [socket, threadId, setupReactionEvents]);
  
  // Public method to track a client-generated reaction
  const trackClientReaction = useCallback((reactionId: string) => {
    debugLog(`[useReactionsRealtime:${instanceId.current}] Tracking client reaction: ${reactionId}`);
    trackClientEntity(reactionId);
  }, [trackClientEntity]);
  
  return {
    isConnected,
    trackClientReaction,
    isClientGenerated,
    debug: {
      enableDebug: () => {
        localStorage.setItem('REALTIME_DEBUG', 'true');
      },
      disableDebug: () => {
        localStorage.removeItem('REALTIME_DEBUG');
      },
      threadId,
      instanceId: instanceId.current,
      isDebugEnabled: DEBUG_MODE
    }
  };
} 